import { type FC, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { BsCaretDownFill, BsTrash } from 'react-icons/bs';

import { type TimelineSlide } from '@editor2/types/pageElements';
import IconButton from '@editor2/components/IconButton';
import CopyInput from '@editor2/components/CopyInputWrapper';
import DatePickerBlock from './DatePickerBlock';
import { timelineDateToDate, updateTimelineDate } from './helpers';

interface Props {
	slide: TimelineSlide;
	isTitleSlide: boolean;
	onDeleteSlideClick: () => void;
	onSlideUpdate: (_) => void;
}

const SlideEditView: FC<Props> = (props) => {
	const { slide, isTitleSlide, onDeleteSlideClick, onSlideUpdate } = props;

	const [slideProperties, setSlideProperties] = useState({ ...slide });
	const [backgroundType, setBackgroundType] = useState(
		slideProperties.background?.color ? 'color' : 'url'
	);

	const [collapsed, setCollapsed] = useState(true);

	useEffect(() => {
		setSlideProperties({ ...slide });
	}, [slide]);

	const onChange = (value) => {
		setSlideProperties({ ...slideProperties, ...value });
		onSlideUpdate({ ...slideProperties, ...value });
	};

	return (
		<>
			<Form>
				<Form.Group>
					<Form.Label>Start date {!isTitleSlide && `(*required)`}</Form.Label>
					<DatePickerBlock
						date_format={slideProperties.start_date_format}
						onFormatChange={(value) => {
							const date = slideProperties.start_date
								? timelineDateToDate(slideProperties.start_date)
								: new Date();
							const updatedStartDate = updateTimelineDate(date, value);
							onChange({ start_date_format: value, start_date: updatedStartDate });
						}}
						date={slideProperties.start_date}
						onDateChange={(date) => {
							const updatedStartDate = updateTimelineDate(date, slideProperties.start_date_format);
							onChange({ start_date: updatedStartDate });
						}}
					/>
				</Form.Group>
				<Form.Group>
					<Form.Label>End date</Form.Label>
					<DatePickerBlock
						date_format={slideProperties.end_date_format}
						onFormatChange={(value) => {
							const date = slideProperties.end_date
								? timelineDateToDate(slideProperties.end_date)
								: new Date();
							const updatedEndDate = updateTimelineDate(date, value);
							onChange({ end_date_format: value, end_date: updatedEndDate });
						}}
						date={slideProperties.end_date}
						onDateChange={(date) => {
							const updatedEndDate = updateTimelineDate(date, slideProperties.end_date_format);
							onChange({ end_date: updatedEndDate });
						}}
					/>
				</Form.Group>

				<div className="block">
					<Form.Group className="mediaTextArea">
						<Form.Label>Display date</Form.Label>
						<Form.Control
							value={slideProperties.display_date}
							onChange={(e) => onChange({ display_date: e.target.value })}
						/>
					</Form.Group>

					<Form.Group className="mediaTextArea">
						<Form.Label>Group</Form.Label>
						<Form.Control
							value={slideProperties.group}
							onChange={(e) => onChange({ group: e.target.value })}
						/>
					</Form.Group>
				</div>

				<Form.Group>
					<Form.Label>Headline</Form.Label>
					<Form.Control
						className="title"
						value={slideProperties.text?.headline}
						onChange={(e) =>
							onChange({ text: { ...slideProperties.text, headline: e.target.value } })
						}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>Text</Form.Label>
					<Form.Control
						as="textarea"
						rows={3}
						value={slideProperties.text?.text}
						onChange={(e) => onChange({ text: { ...slideProperties.text, text: e.target.value } })}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>Media (url)</Form.Label>
					<Form.Control
						value={slideProperties.media?.url}
						onChange={(e) => onChange({ media: { ...slideProperties.media, url: e.target.value } })}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>Media thumbnails</Form.Label>
					<Form.Control
						value={slideProperties.media?.thumbnail}
						onChange={(e) =>
							onChange({ media: { ...slideProperties.media, thumbnail: e.target.value } })
						}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>Media alt tag</Form.Label>
					<Form.Control
						as="textarea"
						rows={1}
						value={slideProperties.media?.alt}
						onChange={(e) => onChange({ media: { ...slideProperties.media, alt: e.target.value } })}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>Media credit</Form.Label>
					<Form.Control
						as="textarea"
						rows={2}
						value={slideProperties.media?.credit}
						onChange={(e) =>
							onChange({ media: { ...slideProperties.media, credit: e.target.value } })
						}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>Media caption</Form.Label>
					<Form.Control
						as="textarea"
						rows={2}
						value={slideProperties.media?.caption}
						onChange={(e) =>
							onChange({ media: { ...slideProperties.media, caption: e.target.value } })
						}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>Background</Form.Label>
					<div className="block">
						<Form.Control
							as="select"
							className="select"
							value={backgroundType}
							onChange={(e) => setBackgroundType(e.target.value)}>
							<option key={0} value={'color'}>
								color
							</option>
							<option key={1} value={'url'}>
								url
							</option>
						</Form.Control>
						<Form.Control
							value={slideProperties.background && slideProperties.background[backgroundType]}
							onChange={(e) =>
								onChange({
									background: {
										[backgroundType]: e.target.value
									}
								})
							}
						/>
					</div>
				</Form.Group>

				{backgroundType === 'url' && (
					<Form.Group>
						<Form.Label>Background alt tag</Form.Label>
						<Form.Control
							as="textarea"
							rows={1}
							value={slideProperties.background?.alt}
							onChange={(e) =>
								onChange({
									background: { ...slideProperties.background, alt: e.target.value }
								})
							}
						/>
					</Form.Group>
				)}

				<div className="additionalFieldsWrapper">
					<div className="additionalFieldsHeader">
						<div>Additional fields</div>

						<BsCaretDownFill
							onClick={() => setCollapsed(!collapsed)}
							className={collapsed ? 'collapseIconRotate' : 'collapseIcon'}
						/>
					</div>

					{!collapsed && (
						<div className="additionalFieldsBlock">
							<Form.Group>
								<Form.Label>Media title</Form.Label>
								<Form.Control
									value={slideProperties.media?.title}
									onChange={(e) =>
										onChange({ media: { ...slideProperties.media, title: e.target.value } })
									}
								/>
							</Form.Group>
							<Form.Group>
								<Form.Label>Media link</Form.Label>
								<Form.Control
									value={slideProperties.media?.link}
									onChange={(e) =>
										onChange({ media: { ...slideProperties.media, link: e.target.value } })
									}
								/>
							</Form.Group>
							<Form.Group>
								<Form.Label>Media link target</Form.Label>
								<Form.Control
									value={slideProperties.media?.link_target}
									onChange={(e) =>
										onChange({ media: { ...slideProperties.media, link_target: e.target.value } })
									}
								/>
							</Form.Group>

							<CopyInput label="Unique id" value={slideProperties.unique_id} />
						</div>
					)}
				</div>
			</Form>

			<div className="deleteSlideButton">
				<IconButton onClick={onDeleteSlideClick}>
					<BsTrash />
				</IconButton>
			</div>
		</>
	);
};

export default SlideEditView;
